import { Box, Button, Grid } from '@mui/material';
import { FC, ReactNode, useEffect, useRef } from 'react';
import CommonDrawer from '../simple/commonDrawer/commonDrawer';
import ModalHeader from '../simple/modalHeader/modalHeader';
import { useTranslation } from 'src/hooks/useTranslationHook';

type StructuredModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title: string;
  contentId?: string;
  drawerWidth?: string;
  footerPositiveActionText?: string;
  footerNegativeActionText?: string;
  footerPositiveAction?: () => void;
  footerNegativeAction?: () => void;
  footerMessage?: ReactNode;
  isLoading?: boolean;
  footerPositiveActionDisabled?: boolean;
};

const StructuredModal: FC<StructuredModalProps> = (props) => {
  const { translateText } = useTranslation();
  const {
    isOpen,
    onClose,
    children,
    title,
    contentId = 'commonModalContent',
    drawerWidth = '50vw',
    footerPositiveAction,
    footerNegativeAction,
    footerPositiveActionText,
    footerNegativeActionText,
    footerMessage,
    isLoading = false,
    footerPositiveActionDisabled = false,
    ...other
  } = props;

  const defaultPositiveText = translateText('common.save');
  const defaultNegativeText = translateText('common.cancel');
  const hasPushedStateRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasPushedStateRef.current) {
      // Push a state when modal opens
      window.history.pushState({ modalOpen: true }, '');
      hasPushedStateRef.current = true;
    }

    const handlePopState = () => {
      if (isOpen) {
        // Push state back immediately to prevent navigation
        window.history.pushState({ modalOpen: true }, '');
      } else {
        hasPushedStateRef.current = false;
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Clean up: if modal closes, reset the ref
      if (!isOpen) {
        hasPushedStateRef.current = false;
      }
    };
  }, [isOpen]);

  return (
    <CommonDrawer
      anchor="right"
      open={isOpen}
      drawerWidth={drawerWidth}
      id="structuredModal"
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            md: drawerWidth,
            lg: drawerWidth,
            xl: drawerWidth,
          },
          maxWidth: {
            xs: 'none',
            sm: 'none',
            md: 'none',
            lg: 'none',
            xl: 'none',
          },
          borderRadius: {
            xs: '0 !important',
            sm: 'inherit',
            md: 'inherit',
            lg: 'inherit',
            xl: 'inherit',
          },
          marginRight: {
            xs: '0 !important',
            sm: 0,
            md: 0,
            lg: 0,
            xl: 0,
          },
        },
      }}
      {...other}
    >
      <Grid
        container
        sx={{
          height: '100%',
          py: 0,
          overflow: 'hidden',
        }}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          styles={{ paddingLeft: 6, paddingRight: 6 }}
        />
        <Grid
          item
          xs={12}
          id={contentId}
          sx={{
            height: `calc(100% - var(--modal-header-height) - var(--modal-footer-height))`,
            overflow: 'hidden',
            overflowY: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
            pb: 2,
            '& .MuiGrid-container': {
              '@media (max-width: 600px)': {
                margin: '0 !important',
                '& > .MuiGrid-item': {
                  padding: '12px !important',
                },
              },
            },
          }}
          data-testid="structured-modal-content"
        >
          {children}
        </Grid>
        {(footerMessage || footerNegativeAction || footerPositiveAction) && (
          <Grid
            item
            id="footer"
            xs={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: `${footerMessage ? 'space-between' : 'flex-end'}`,
              height: 'var(--modal-footer-height)',
              borderTop: '1px solid var(--border_grey)',
              px: { xs: 2, sm: 3, md: 4 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
              py: { xs: 2, sm: 0 },
            }}
            data-testid="structured-modal-footer"
          >
            {footerMessage}
            <Box
              data-testid="footer-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                justifyContent: 'flex-end',
                height: '100%',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {footerNegativeAction && (
                <Button
                  variant="outlined"
                  onClick={footerNegativeAction}
                  disabled={isLoading}
                  size="medium"
                  data-testid="modal-cancel-button"
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {footerNegativeActionText || defaultNegativeText}
                </Button>
              )}
              {footerPositiveAction && (
                <Button
                  variant="contained"
                  onClick={footerPositiveAction}
                  size="medium"
                  disabled={isLoading || footerPositiveActionDisabled}
                  data-testid="modal-save-button"
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {footerPositiveActionText || defaultPositiveText}
                </Button>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </CommonDrawer>
  );
};

export default StructuredModal;
